import { useState, useCallback } from "react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { siteData, ExtProject } from "@/store/siteData";
import { saveImage, getImageUrl, getYouTubeEmbedUrl, deleteImage } from "@/utils/imageDB";
import { useImageUrl } from "@/hooks/useImageUrl";
import { useGalleryUrls } from "@/hooks/useGalleryUrls";
import { isFeatureEnabled, getFeatureVisibility } from "@/pages/dev-panel/page";
import { PremiumPopup, getEditablePopupConfig } from "@/components/feature/PremiumLockedButton";

const typeOptions = ["Konut", "Ticari", "Karma Kullanım", "Endüstriyel"];

const emptyProject: ExtProject = {
  id: 0, name: "Yeni Proje", location: "", units: 0, status: "Planlama",
  statusColor: "planning", year: "2026", area: "0 m²", type: "Konut",
  description: "", features: [], image: "", youtubeUrl: "", googleMapsUrl: "",
  galleryIds: [], archived: false,
};

// ── Field ────────────────────────────────────────────────────────────────────

function Field({ label, value, onChange, type = "text", multiline = false, hint }: {
  label: string; value: string | number; onChange: (v: string) => void;
  type?: string; multiline?: boolean; hint?: string;
}) {
  const cls = "w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors";
  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      {multiline
        ? <textarea value={String(value)} onChange={e => onChange(e.target.value)} rows={3} className={`${cls} resize-none`} />
        : <input type={type} value={String(value)} onChange={e => onChange(e.target.value)} className={cls} />}
      {hint && <p className="text-[#5E7398] text-xs mt-1">{hint}</p>}
    </div>
  );
}

// ── Gallery ──────────────────────────────────────────────────────────────────

function SortableGalleryThumb({ imageId, previewUrl, onRemove }: { imageId: string; previewUrl: string; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: imageId });
  const url = useImageUrl(imageId, previewUrl);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative group aspect-square rounded-lg overflow-hidden border border-[#2B3654] ${isDragging ? "opacity-50 shadow-lg z-50" : ""}`}
    >
      <div className="absolute inset-0 cursor-grab active:cursor-grabbing z-10" {...attributes} {...listeners}></div>
      <img src={url} alt="" className="w-full h-full object-cover" />
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-600/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-20"
      >
        <i className="ri-close-line text-xs"></i>
      </button>
      <div className="absolute bottom-1 left-1 w-5 h-5 flex items-center justify-center bg-black/50 rounded opacity-0 group-hover:opacity-100 transition-opacity z-20">
        <i className="ri-drag-move-2-line text-white text-xs"></i>
      </div>
    </div>
  );
}

function GalleryEditor({ galleryIds, onUpdate }: { galleryIds: string[]; onUpdate: (ids: string[]) => void }) {
  const galleryUrls = useGalleryUrls(galleryIds);
  const [uploading, setUploading] = useState(false);

  const gallerySensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleGalleryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = galleryIds.indexOf(active.id as string);
    const newIdx = galleryIds.indexOf(over.id as string);
    onUpdate(arrayMove(galleryIds, oldIdx, newIdx));
  };

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    const newIds: string[] = [];
    for (const file of Array.from(files)) newIds.push(await saveImage(file));
    onUpdate([...galleryIds, ...newIds]);
    setUploading(false);
  };

  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">
        Fotoğraf Galerisi
        <span className="text-[#5E7398] normal-case font-normal tracking-normal ml-1">({galleryIds.length} resim{galleryIds.length > 1 ? " — sürükleyerek sırala" : ""})</span>
      </label>
      {galleryIds.length > 0 && (
        <DndContext sensors={gallerySensors} collisionDetection={closestCenter} onDragEnd={handleGalleryDragEnd}>
          <SortableContext items={galleryIds} strategy={verticalListSortingStrategy}>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {galleryIds.map((id, idx) => (
                <SortableGalleryThumb
                  key={id}
                  imageId={id}
                  previewUrl={galleryUrls[idx] || ""}
                  onRemove={async () => { await deleteImage(id); onUpdate(galleryIds.filter((_, i) => i !== idx)); }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <label className={`flex flex-col items-center gap-2 cursor-pointer border-2 border-dashed rounded-lg px-4 py-4 transition-colors ${uploading ? "border-[#3B82F6] bg-[#3B82F6]/5" : "border-[#2B3654] hover:border-[#3B82F6]"}`}>
        <div className="w-9 h-9 flex items-center justify-center bg-[#3B82F6]/20 rounded-full">
          {uploading ? <i className="ri-loader-4-line text-[#3B82F6] text-lg animate-spin"></i> : <i className="ri-image-add-line text-[#3B82F6] text-lg"></i>}
        </div>
        <span className="text-[#7B8FAF] text-xs text-center">{uploading ? "Yükleniyor…" : "PNG, JPG yükle"}</span>
        <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={e => e.target.files && handleFiles(e.target.files)} />
      </label>
    </div>
  );
}

// ── Project Form ──────────────────────────────────────────────────────────────

function ProjectForm({ project, onSave, onDelete, onCancel, onToggleArchive, statusTagsEnabled = true }: {
  project: ExtProject;
  onSave: (p: ExtProject, action: string, details: string) => void;
  onDelete: () => void;
  onCancel: () => void;
  onToggleArchive?: (p: ExtProject) => void;
  statusTagsEnabled?: boolean;
}) {
  const [data, setData] = useState<ExtProject>({ galleryIds: [], ...project });
  const [featInput, setFeatInput] = useState("");
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const imgUrl = useImageUrl(data.imageId, data.image);

  const statusTags = siteData.getStatusTags();
  const upd = useCallback((key: keyof ExtProject, val: unknown) => setData(p => ({ ...p, [key]: val })), []);

  const handleImageUpload = async (file: File) => {
    const id = await saveImage(file);
    const url = await getImageUrl(id);
    setData(p => ({ ...p, imageId: id }));
    if (url) setImgPreview(url);
  };

  const addFeature = () => {
    if (!featInput.trim()) return;
    setData(p => ({ ...p, features: [...p.features, featInput.trim()] }));
    setFeatInput("");
  };

  const ytEmbed = getYouTubeEmbedUrl(data.youtubeUrl || "");

  const handleSave = () => {
    const isNew = project.id === 0;
    onSave(data, isNew ? "Eklendi" : "Düzenlendi", data.name);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold flex items-center gap-2">
          {data.id === 0 ? "Yeni Proje" : data.name}
          {data.archived && (
            <span className="text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-full">Arşivde</span>
          )}
        </h3>
        <button onClick={onCancel} className="text-[#7B8FAF] hover:text-white cursor-pointer w-8 h-8 flex items-center justify-center">
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>

      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Kapak Görseli</label>
        <div className="flex items-start gap-4">
          <div className="relative w-32 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-[#2B3654] bg-[#0F1729] group">
            <img src={imgPreview || imgUrl || "https://readdy.ai/api/search-image?query=modern%20building%20architecture&width=300&height=200&seq=placeholder&orientation=landscape"} alt="" className="w-full h-full object-cover" />
            {(imgPreview || data.imageId) && (
              <button
                type="button"
                onClick={() => { setData(p => ({ ...p, imageId: undefined })); setImgPreview(null); }}
                className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-600/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            )}
          </div>
          <label className="flex-1 flex flex-col items-center gap-2 cursor-pointer border-2 border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-lg px-4 py-4 transition-colors">
            <div className="w-8 h-8 flex items-center justify-center bg-[#3B82F6]/20 rounded-full">
              <i className="ri-upload-cloud-2-line text-[#3B82F6] text-lg"></i>
            </div>
            <span className="text-[#7B8FAF] text-xs text-center">PNG, JPG yükle{data.imageId ? " (yüklendi ✓)" : ""}</span>
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0])} />
          </label>
        </div>
      </div>

      <GalleryEditor galleryIds={data.galleryIds || []} onUpdate={ids => upd("galleryIds", ids)} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Proje Adı" value={data.name} onChange={v => upd("name", v)} />
        <Field label="Konum" value={data.location} onChange={v => upd("location", v)} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Teslim Yılı" value={data.year} onChange={v => upd("year", v)} />
        <Field label="Alan" value={data.area} onChange={v => upd("area", v)} />
        <Field label="Konut Adedi" value={data.units} onChange={v => upd("units", Number(v))} type="number" />
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Tip</label>
          <select value={data.type} onChange={e => upd("type", e.target.value)} className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] cursor-pointer">
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {statusTagsEnabled && (
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Durum</label>
          <div className="flex gap-2 flex-wrap">
            {statusTags.map(s => (
              <button key={s.id} onClick={() => setData(p => ({ ...p, status: s.label, statusColor: s.id }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${data.statusColor === s.id ? "text-white" : "text-[#7B8FAF] border-[#2B3654] hover:text-white"}`}
                style={data.statusColor === s.id ? { background: s.color, borderColor: s.color } : {}}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: data.statusColor === s.id ? "rgba(255,255,255,0.5)" : s.color }}></span>
                {s.label}
              </button>
            ))}
            {statusTags.length === 0 && (
              <p className="text-[#7B8FAF] text-xs py-2">Etiket grubu yok. Site Ayarları &rarr; Durum Etiketleri bölümünden ekleyin.</p>
            )}
          </div>
        </div>
      )}

      <Field label="Açıklama" value={data.description} onChange={v => upd("description", v)} multiline />

      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Özellikler</label>
        <div className="flex gap-2 mb-2">
          <input type="text" value={featInput} onChange={e => setFeatInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addFeature()} placeholder="Özellik yaz, Enter'a bas"
            className="flex-1 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
          <button onClick={addFeature} className="px-4 py-2 bg-[#3B82F6]/20 text-[#B8C5D8] border border-[#3B82F6]/40 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-[#3B82F6]/40 transition-colors">Ekle</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.features.map((f, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs bg-[#1E2B4A] text-[#B8C5D8] border border-[#2B3654] px-3 py-1 rounded-full">
              {f}
              <button onClick={() => setData(p => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }))} className="text-[#5E7398] hover:text-red-400 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
            </span>
          ))}
        </div>
      </div>

      <div>
        <Field label="YouTube Video URL" value={data.youtubeUrl || ""} onChange={v => upd("youtubeUrl", v)} hint="youtube.com/watch?v=... linki yapıştırın" />
        {ytEmbed && (
          <div className="mt-3 relative w-full rounded-lg overflow-hidden border border-[#2B3654]" style={{ paddingBottom: "40%" }}>
            <iframe src={ytEmbed} title="Önizleme" className="absolute inset-0 w-full h-full" allowFullScreen></iframe>
          </div>
        )}
      </div>

      <div>
        <Field label="Google Maps URL" value={data.googleMapsUrl || ""} onChange={v => upd("googleMapsUrl", v)} hint="Google Maps > Paylaş > Haritayı yerleştir > src kısmındaki URL" />
        {data.googleMapsUrl && (
          <div className="mt-3 h-40 rounded-lg overflow-hidden border border-[#2B3654]">
            <iframe src={data.googleMapsUrl} title="Harita" className="w-full h-full" loading="lazy"></iframe>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 pt-4 border-t border-[#2B3654]">
        <button onClick={handleSave}
          className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
          {saved ? "✓ Kaydedildi" : "Kaydet"}
        </button>
        {data.id !== 0 && (
          <>
            {onToggleArchive ? (
              <button onClick={() => onToggleArchive(data)}
                className={`px-4 py-2.5 text-sm border rounded-lg transition-all cursor-pointer whitespace-nowrap ${data.archived ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" : "border-orange-500/30 text-orange-400 hover:bg-orange-500/10"}`}>
                <i className={`${data.archived ? "ri-eye-line" : "ri-archive-line"} mr-1.5`}></i>
                {data.archived ? "Gizlemeyi Kaldır" : "Arşivle / Gizle"}
              </button>
            ) : getFeatureVisibility("project_archive") === "lock" ? (
              <button className="px-4 py-2.5 text-sm border border-amber-500/30 text-amber-400 rounded-lg whitespace-nowrap opacity-70 cursor-not-allowed flex items-center gap-1.5">
                <i className="ri-lock-line"></i> Arşivle (Premium)
              </button>
            ) : null}
            <button onClick={onDelete}
              className="px-4 py-2.5 text-sm border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-delete-bin-line mr-1.5"></i>Sil
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Sortable Project Row ──────────────────────────────────────────────────────

function SortableProjectRow({ project, active, onClick, showDragHandle = true }: {
  project: ExtProject; active: boolean; onClick: () => void; showDragHandle?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: project.id });

  const imgUrl = useImageUrl(project.imageId, project.image);
  const statusTags = siteData.getStatusTags();
  const tagInfo = statusTags.find(t => t.id === project.statusColor);
  const tagColor = tagInfo?.color || "#94A3B8";

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 p-2.5 rounded-lg transition-all ${active ? "bg-[#3B82F6]/15 border border-[#3B82F6]/40" : "hover:bg-white/5 border border-transparent"} ${isDragging ? "opacity-50 shadow-lg" : ""} ${project.archived ? "opacity-50" : ""}`}
    >
      {showDragHandle && (
        <button
          {...attributes}
          {...listeners}
          className="w-5 h-5 flex items-center justify-center text-[#465886] hover:text-[#7B8FAF] cursor-grab active:cursor-grabbing flex-shrink-0"
        >
          <i className="ri-draggable text-sm"></i>
        </button>
      )}
      <button onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer">
        <div className="w-10 h-8 flex-shrink-0 rounded overflow-hidden bg-[#1E2B4A]">
          <img src={imgUrl} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{project.name}</p>
          <p className="text-xs font-medium" style={{ color: tagColor }}>{project.status}</p>
        </div>
      </button>
      <div className="flex items-center gap-1 flex-shrink-0">
        {project.archived && (
          <div className="w-5 h-5 flex items-center justify-center" title="Arşivde">
            <i className="ri-eye-off-line text-orange-400 text-xs"></i>
          </div>
        )}
        {(project.galleryIds?.length ?? 0) > 0 && (
          <span className="text-[#7B8FAF] text-xs bg-[#1E2B4A] px-1.5 py-0.5 rounded">
            <i className="ri-image-line"></i> {project.galleryIds?.length}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

interface ProjectsEditorProps {
  initialId?: number | null;
}

type ViewMode = "active" | "archived" | "all";
type SortMode = "custom" | "year_asc" | "year_desc";

export default function ProjectsEditor({ initialId }: ProjectsEditorProps) {
  const [projects, setProjects] = useState<ExtProject[]>(siteData.getProjects());
  const [selectedId, setSelectedId] = useState<number | null>(initialId ?? null);
  const [isNew, setIsNew] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("active");
  const [sortMode, setSortMode] = useState<SortMode>("custom");

  const dragDropEnabled   = isFeatureEnabled("drag_drop_projects");
  const archiveEnabled    = isFeatureEnabled("project_archive");
  const archiveVisibility = getFeatureVisibility("project_archive");
  const statusTagsEnabled = isFeatureEnabled("status_tags");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activeProjects   = projects.filter(p => !p.archived);
  const archivedProjects = projects.filter(p => p.archived);
  const baseList = viewMode === "active" ? activeProjects
                 : viewMode === "archived" ? archivedProjects
                 : projects;

  const displayList = !dragDropEnabled && sortMode !== "custom"
    ? [...baseList].sort((a, b) => {
        const ay = parseInt(String(a.year)) || 0;
        const by = parseInt(String(b.year)) || 0;
        return sortMode === "year_asc" ? ay - by : by - ay;
      })
    : baseList;

  const selected = isNew ? null : projects.find(p => p.id === selectedId) ?? null;

  const persist = useCallback((next: ExtProject[], action: string, details: string) => {
    const withOrder = next.map((p, i) => ({ ...p, sortOrder: i }));
    setProjects(withOrder);
    siteData.saveProjects(withOrder, action, details);
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!dragDropEnabled) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = displayList.map(p => p.id);
    const oldIndex = ids.indexOf(active.id as number);
    const newIndex = ids.indexOf(over.id as number);
    const reordered = arrayMove(displayList, oldIndex, newIndex);
    const otherProjects = projects.filter(p => !displayList.find(d => d.id === p.id));
    persist([...reordered, ...otherProjects], "Sıralandı", `${reordered.length} proje yeniden sıralandı`);
  };

  const handleSave = (updated: ExtProject, action: string, details: string) => {
    let next: ExtProject[];
    if (isNew) {
      const newId = Math.max(...projects.map(p => p.id), 0) + 1;
      const newP = { ...updated, id: newId, sortOrder: projects.length };
      next = [...projects, newP];
      setSelectedId(newId);
      setIsNew(false);
    } else {
      next = projects.map(p => p.id === updated.id ? updated : p);
    }
    persist(next, action, details);
  };

  const handleDelete = () => {
    if (!selectedId) return;
    const proj = projects.find(p => p.id === selectedId);
    persist(projects.filter(p => p.id !== selectedId), "Silindi", proj?.name || "");
    setSelectedId(null);
  };

  const handleToggleArchive = (p: ExtProject) => {
    const updated = { ...p, archived: !p.archived };
    const next = projects.map(pr => pr.id === p.id ? updated : pr);
    persist(next, updated.archived ? "Arşivlendi" : "Arşivden Çıkarıldı", p.name);
    setSelectedId(p.id);
    setIsNew(false);
  };

  const viewTabs: { id: ViewMode; label: React.ReactNode; locked?: boolean }[] = [
    { id: "active", label: `Aktif (${activeProjects.length})` },
    { id: "archived", locked: !archiveEnabled, label: (
      <span className="flex items-center gap-1">
        Arşiv ({archivedProjects.length})
        {!archiveEnabled && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-semibold whitespace-nowrap">
            <i className="ri-lock-line text-[10px]"></i> Premium
          </span>
        )}
      </span>
    ) },
    { id: "all", label: "Tümü" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Projeler</h1>
          <p className="text-[#7B8FAF] text-sm mt-0.5">
            {activeProjects.length} aktif
            {archiveEnabled || archiveVisibility === "locked" ? ` · ${archivedProjects.length} arşivde` : ""}
            {dragDropEnabled ? " · Sürükleyerek sırala" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!dragDropEnabled && (
            <select
              value={sortMode}
              onChange={e => setSortMode(e.target.value as SortMode)}
              className="bg-[#0F1729] border border-[#2B3654] text-[#A5B5CC] text-xs rounded-lg px-3 py-2 outline-none cursor-pointer"
            >
              <option value="custom">Varsayılan Sıra</option>
              <option value="year_desc">Tarih: Yeniden Eskiye</option>
              <option value="year_asc">Tarih: Eskiden Yeniye</option>
            </select>
          )}
          <button onClick={() => { setSelectedId(null); setIsNew(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white text-sm rounded-lg hover:bg-[#3d4e6a] transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
            Yeni Proje
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 bg-[#141E35] border border-[#2B3654] rounded-xl overflow-hidden">
          <div className="flex border-b border-[#2B3654] px-2 pt-2">
            {viewTabs.map(tab => (
              <button key={tab.id} onClick={() => setViewMode(tab.id)}
                className={`px-3 py-2 text-xs font-medium rounded-t transition-all cursor-pointer whitespace-nowrap ${viewMode === tab.id ? "text-white border-b-2 border-[#3B82F6]" : "text-[#7B8FAF] hover:text-white"}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-2 space-y-0.5 max-h-[70vh] overflow-y-auto">
            {viewMode === "archived" && !archiveEnabled ? (
              <div className="py-6 w-full flex items-center justify-center">
                <PremiumPopup config={getEditablePopupConfig("project_archive")} visible={true} inline={true} />
              </div>
            ) : dragDropEnabled && viewMode !== "archived" ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={displayList.map(p => p.id)} strategy={verticalListSortingStrategy}>
                  {displayList.map(p => (
                    <SortableProjectRow key={p.id} project={p} active={!isNew && selectedId === p.id}
                      onClick={() => { setSelectedId(p.id); setIsNew(false); }} showDragHandle={dragDropEnabled} />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              displayList.map(p => (
                <SortableProjectRow key={p.id} project={p} active={!isNew && selectedId === p.id}
                  onClick={() => { setSelectedId(p.id); setIsNew(false); }} showDragHandle={false} />
              ))
            )}
            {displayList.length === 0 && (viewMode !== "archived" || archiveEnabled) && (
              <div className="py-10 text-center">
                <i className="ri-inbox-line text-3xl text-[#3B82F6]"></i>
                <p className="text-[#7B8FAF] text-sm mt-2">
                  {viewMode === "archived" ? "Arşivde proje yok" : "Proje bulunamadı"}
                </p>
              </div>
            )}
          </div>

          {dragDropEnabled && viewMode === "active" && displayList.length > 1 && (
            <div className="px-4 py-2.5 border-t border-[#1E2B4A] bg-[#0F1729]/50">
              <p className="text-[#465886] text-xs flex items-center gap-1.5">
                <i className="ri-drag-move-2-line text-sm"></i>
                Sol tutacağından sürükle → sıralamayı değiştir
              </p>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 bg-[#141E35] border border-[#2B3654] rounded-xl p-6 max-h-[75vh] overflow-y-auto">
          {(selected || isNew) ? (
            <ProjectForm
              project={isNew ? emptyProject : selected!}
              onSave={handleSave}
              onDelete={handleDelete}
              onCancel={() => { setSelectedId(null); setIsNew(false); }}
              onToggleArchive={archiveEnabled ? handleToggleArchive : undefined}
              statusTagsEnabled={statusTagsEnabled}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <div className="w-14 h-14 flex items-center justify-center bg-[#0F1729] rounded-xl mb-4">
                <i className="ri-building-2-line text-3xl text-[#3B82F6]"></i>
              </div>
              <p className="text-white font-medium">Proje seçin</p>
              <p className="text-[#7B8FAF] text-sm mt-1">Düzenlenmek için soldan bir proje seçin veya yeni proje ekleyin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
